/* eslint-disable max-lines */
import * as React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { Icon } from '@app/Components/Icon';

import { PersonRequestSummaryStatus, PersonRequestType } from '@app/AppConstants/PersonVacation';
import { DateTime } from '@app/AppConstants/DateTime';
import { Range, RangeReadyStatus } from './VirtualizedRoster/Stores/Range';
import { DayDuty, IDayDuty, DutyItemCache, targetConstructor } from '@app/Models';
import { CapacityLevel, DutyItem, FdpAmendmentType, RosterType } from '@app/Models/WebApiModels';

import { appStore } from '@app/AppStore';

import DateTimeService from '@app/Services/DateTimeService';
import { navigationStore } from '@app/Stores/NavigationStore';
import { DragEffect, RosterElement } from '@app/Components/VirtualizedRoster';
import { IBaseDutyStore } from '@app/Components/VirtualizedRoster/Stores/DutyStore';
import { AzureAuth } from './AzureAuth';
import { ActivityCode } from '@app/AppConstants/ActivityCode';
import { applyDecorators } from "@app/Helpers/Decorator";

export type DoubleClickCallback = (date: Date, personId: string, dutyItem: IDayDuty, duty: DutyItem | null) => boolean;
//TODO: simplity arguments
export type DutyLineRenderDetailsCallback = (date: Date, personId: string, dutyItem: IDayDuty | null, duty: DutyItem | null, focusedElement?: RosterElement | null) => JSX.Element | null;
export type DutyLineRenderExtrasCallback = (date: Date, personId: string, dutyItem: IDayDuty | null, duty: DutyItem | null, focusedElement?: RosterElement | null) => JSX.Element | null;

type RosterLineProps = {
    personId: string;
    personCode?: string;
    dutyDays: Array<Date>;
    index: number;
    dutyStore?: IBaseDutyStore;
    focusedElement?: RosterElement | null;
    personDutyMap?: Map<number, DutyItemCache> | null;
    isBeforeActivePerson?: boolean | null;
    rosterType?: string;
    markedDays?: Array<Date>;
    marker?: string;
    customSelectedDays?: Array<Date>;
    customSelectedClass?: string;
    allowedDateRange?: Array<Range<Date>> | Range<Date> | null;
    showCapacityNumbers?: boolean;
    copiedDuties?: DutyItem[];
    selectedDuties?: RosterElement[];
    onDoubleClick?: DoubleClickCallback;
    onRenderCellDetails?: DutyLineRenderDetailsCallback;
    onRenderCellExtras?: DutyLineRenderExtrasCallback;
    isHistorizedRosterline?: boolean;
    historizeLastDate?: Date | null;
    showSwissTime?: boolean;
    onStartDragDuty?: (sourceElement: Element | null, isAllowCopying?: boolean) => boolean;
    onDragOverDuty?: (targetElement: Element, isCopy: boolean) => DragEffect;
    onDragEndDuty?: () => void;
    onDropDuty?: (targetElement: Element, isCopy: boolean) => void;
    showDutyAsFight?: boolean;
};

type DutyRenderOptions = {
    beforeSelectedDutyDate: Date | null;
    dutyStore?: IBaseDutyStore;
    highlightDate: Date;
};

@observer
class RosterLine extends React.Component<RosterLineProps> {
    @observable private _isDragStart: boolean = false;
    private static readonly newGavFtlSystemApplicableFrom = DateTimeService.date(2024, 5, 1);   // Starting from 1 June 2024

    constructor(props: RosterLineProps) {
        super(props);
        applyDecorators(this);
    }


    private _getCssClasses(dutyItem: IDayDuty, dayDuty: DutyItem | null, isNotReleased: boolean, renderOptions: DutyRenderOptions): string[] {
        const { personId, markedDays, marker, customSelectedDays, customSelectedClass, rosterType, selectedDuties, focusedElement, copiedDuties, isHistorizedRosterline } = this.props;

        const dutyStore = renderOptions.dutyStore;
        const highlightDate = renderOptions.highlightDate;
        const beforeSelectedDutyDate = renderOptions.beforeSelectedDutyDate;

        const dutyDay = dutyItem.dutyDay;
        const itemClass: string[] = [];
        const hasMarkers = markedDays && markedDays.length;
        const isFullRosterType: boolean = rosterType === RosterType.Full && !hasMarkers;
        const isCopied = isFullRosterType && copiedDuties?.find(copiedDuty => copiedDuty.personId === personId && DateTimeService.isSameDate(copiedDuty.date, dutyDay));

        const focusedDate = focusedElement && new Date(focusedElement.dateUtc);
        const focusedPersonId = focusedElement && focusedElement.personId;
        if (focusedElement && focusedDate && dutyDay) {
            if (DateTimeService.isSameDate(focusedDate, dutyDay)) itemClass.push('active');
            if (beforeSelectedDutyDate && DateTimeService.isSameDate(beforeSelectedDutyDate, dutyDay)) itemClass.push('beforeActive');
        }
        if (selectedDuties?.length) {
            const selected = selectedDuties.findIndex((item) => {
                return item.personId === personId && item.dateUtc === dutyDay.getTime();
            });
            if (selected > -1) itemClass.push('selected');
        } else if (isFullRosterType && focusedDate) {
            if (DateTimeService.isSameDate(focusedDate, dutyDay) && focusedPersonId === personId) itemClass.push('selected');
        } else if (hasMarkers) {
            if (markedDays?.find((x) => x.getTime() === dutyDay.getTime())) {
                itemClass.push('marked');
                dutyItem.flightTitle = marker ? marker : dutyItem.flightTitle;
            }
        }
        if (isCopied && !this._isDragStart) {
            itemClass.push('buffered');
        }
        if (isFullRosterType && dayDuty && !dayDuty.isValid) itemClass.push('invalid');
        if (dayDuty?.isPlanningPeriodHidden) {
            itemClass.push('pp-hidden');
        }

        let isNotReady = false;
        if (dutyStore) {
            const dutyStatus = dutyStore.getDutyStatus(personId, dutyDay);
            if (dutyStatus === RangeReadyStatus.Loading || dutyStatus === RangeReadyStatus.None) {
                itemClass.push('loading');
                isNotReady = true;
            }
            if (dutyStatus === RangeReadyStatus.Error) {
                itemClass.push('loading-error');
                isNotReady = true;
            }
        }

        if (dayDuty?.isLoading) {
            itemClass.push('loading');
            isNotReady = true;
        }

        const hasCustomHighlighting = customSelectedDays && customSelectedDays.length > 0;
        if (hasCustomHighlighting && customSelectedDays) {
            const isDayToHighlite = customSelectedDays.find((x) => DateTimeService.isSameDate(x, dutyDay));
            if (customSelectedClass && isDayToHighlite) {
                itemClass.push(customSelectedClass);
            }
            if (!customSelectedClass && !isNotReady) {
                if (!isDayToHighlite) {
                    itemClass.push('past');
                }
            }
        }

        if (!(hasCustomHighlighting && !customSelectedClass) && !isNotReady) {
            if (highlightDate.getTime() > dutyDay.getTime()) itemClass.push('past');
        }

        if (this._isDateAllowed(dutyDay)) {
            itemClass.push('date-allowed');
        }

        itemClass.push('activityCode_' + dutyItem.codeName);

        const hasNoDuties = !dutyItem?.hasDuties;
        if (dayDuty) {
            if (hasNoDuties && !dayDuty.shadowDutyType && dayDuty.planningNote && dayDuty.planningNote.length > 20) {
                itemClass.push('duty-note');
            }

            if (hasNoDuties && dayDuty.shadowDutyType) {
                if (dayDuty.shadowDutyType === 'Off') {
                    if (!dayDuty.isShadowOffOptional || rosterType === RosterType.Full || rosterType === RosterType.Person) {
                        itemClass.push('duty-shadow-type-off');
                    }
                }
                if (dayDuty.shadowDutyType === 'Lay') {
                    itemClass.push('duty-shadow-type-lay');
                }
            }

            if (!dayDuty.shadowDutyType && dayDuty.shadowOffType && !dayDuty.isFtl && !dayDuty.isGav) {
                itemClass.push('duty-shadow-off-type');
            }

            if (this._isShowCapacity(dutyItem, dayDuty)) {
                const capacityClass = this._getCapacityLevelClass(dayDuty, rosterType);
                if (capacityClass) {
                    itemClass.push(capacityClass);
                }
            }

            if (dayDuty.isRsvHidden) {
                const hasSbs = dayDuty.activities.find(a => a.activityCodeName === ActivityCode.SBS);
                const hasSbcOrSbl = dayDuty.activities.find(a => a.activityCodeName === ActivityCode.SBC || a.activityCodeName === ActivityCode.SBL);
                if (hasSbs) {
                    itemClass.push('rsv-hidden_SBS');
                } else if (hasSbcOrSbl) {
                    itemClass.push('rsv-hidden_SBC_SBL');
                } else {
                    itemClass.push('rsv-hidden');
                }
            }

            if (dayDuty.isCrewHidden) {
                itemClass.push('crew-hidden');
            }
            if (dayDuty.isPlan) {
                itemClass.push('planned');
            }
        }

        itemClass.unshift(dutyItem.dutyClass);
        if (isNotReleased) {
            itemClass.length = 0;
            itemClass.push('not-released');
        }

        if (isHistorizedRosterline) {
            itemClass.push('selection-disabled', 'historized-rosterline');
        }

        return itemClass;
    }

    private _dutyDoubleClickHandler = (e: React.MouseEvent) => {
        const { personId, dutyStore } = this.props;
        const personDutyMap = this.props.personDutyMap || (dutyStore && dutyStore.dutyList.get(personId));

        const target = e.currentTarget;
        if (!target) return;

        const personDate = target.getAttribute('data-date');
        if (!personDate) return;
        const dutyDay = DateTimeService.parse(personDate, DateTime.jsonDateFormat);
        const dutyItemCache = (personDutyMap && personDutyMap.get(dutyDay.getTime())) || null;
        const dayDuty = dutyItemCache && dutyItemCache.dayDuty ? dutyItemCache.dayDuty : new DayDuty(dutyDay, dutyItemCache);

        let isDefaultAction = true;
        const hasAccessToCrewPage = AzureAuth.hasRosterAccess() || !!(window as { hasAccessToCrewPage?: boolean }).hasAccessToCrewPage;

        if (this.props.onDoubleClick) {
            isDefaultAction = this.props.onDoubleClick(dutyDay, personId, dayDuty, dutyItemCache);
        }
        if (isDefaultAction && hasAccessToCrewPage) {
            const baseUrl = appStore.isCrewPortalDns ? window.location.origin.replace('crewportal', 'crewpage') : '';
            navigationStore.redirect(`${baseUrl}/?personId=${personId}&date=${DateTimeService.toQueryParam(dutyDay)}`);
        }
    };

    public render() {
        const {
            personId,
            personCode,
            dutyDays,
            index,
            rosterType,
            markedDays,
            focusedElement,
            dutyStore,
            onRenderCellExtras,
            isHistorizedRosterline,
            historizeLastDate,
            showDutyAsFight,
            showSwissTime,
            showCapacityNumbers,
        } = this.props;
        const personDutyMap = this.props.personDutyMap || (dutyStore && dutyStore.dutyList.get(personId));
        const dutyItems: JSX.Element[] = [];
        const isFullRosterType: boolean = rosterType === RosterType.Full && !(markedDays && markedDays.length);
        const isRequestToolRosterType: boolean = rosterType === RosterType.RequestToolOff || rosterType === RosterType.RequestToolFlight || rosterType === RosterType.RequestToolVacCabin || rosterType === RosterType.RequestToolVacCockpit || rosterType === RosterType.RequestToolGeneral;
        const beforeSelectedDutyDate = focusedElement ? DateTimeService.addDays(new Date(focusedElement.dateUtc), -1) : null;

        let highlightDate: Date = new Date();
        let releasedLastDate: Date | null = null;
        let shouldGetHistorizedDates = appStore.isHistorized;
        if (isHistorizedRosterline) {
            const isJeppesenFileMode = !!appStore.jeppesenFileUri;
            const isHistorizedMode = !!appStore.isHistorized;

            if (isJeppesenFileMode && isHistorizedMode) {
                shouldGetHistorizedDates = true;
            } else if (isJeppesenFileMode || isHistorizedMode) {
                shouldGetHistorizedDates = false;
            } else {
                shouldGetHistorizedDates = !appStore.isHistorized;
            }
        }
        if (shouldGetHistorizedDates) {
            if (historizeLastDate) {
                releasedLastDate = historizeLastDate;
                highlightDate = DateTimeService.addDays(releasedLastDate, 1);
            }
            if (appStore.releasedDate && !isHistorizedRosterline) {
                highlightDate = appStore.releasedDate;

                releasedLastDate = DateTimeService.addDays(DateTimeService.addMonths(highlightDate, 1), -1);
                if (releasedLastDate.getUTCFullYear() >= 2022 && releasedLastDate.getUTCMonth() + 1 === 12) {
                    releasedLastDate = DateTimeService.addDays(releasedLastDate, 1);
                }
            }
        }
        highlightDate.setUTCHours(0, 0, 0, 0);

        const renderOptions: DutyRenderOptions = {
            beforeSelectedDutyDate: beforeSelectedDutyDate,
            dutyStore: dutyStore,
            highlightDate: highlightDate,
        };

        dutyDays.forEach((dutyDay: Date) => {
            let dutyItemCache = (personDutyMap && personDutyMap.get(dutyDay.getTime())) || null;
            const isNotReleased = isFullRosterType && this._isNotReleased(dutyDay, dutyItemCache, releasedLastDate);
            if (isNotReleased) {
                dutyItemCache = null;
            }

            const dayDuty = dutyItemCache && dutyItemCache.dayDuty ? dutyItemCache.dayDuty : new DayDuty(dutyDay, dutyItemCache);
            if (dutyItemCache && !dutyItemCache.dayDuty) {
                dutyItemCache.dayDuty = dayDuty;
            }

            const itemClass = this._getCssClasses(dayDuty, dutyItemCache, !!isNotReleased, renderOptions);
            const isCopied: boolean = itemClass.indexOf('buffered') !== -1 && !this._isDragStart;
            const haveCapacityInfo = dutyItemCache && typeof dutyItemCache.haveCapacity === 'boolean';
            const capacityTotal = dutyItemCache?.capacityTotal || 0;
            const capacityUsed = dutyItemCache?.capacityUsed || 0;
            const capacityRequested = dutyItemCache?.capacityRequested || 0;
            const capacityAvailable = Math.max(0, capacityTotal - capacityUsed);
            const isImportant = dutyItemCache?.isImportant;
            const haveLockSign = dutyItemCache?.isShadowOffOptional === false;
            const hasOffActivity = dutyItemCache?.activities.find(a => a.activityCodeName === ActivityCode.OFF);
            const havePersistedLockSign = haveLockSign && (dutyItemCache?.shadowDutyType === 'None' || hasOffActivity);
            const convertedDateToJson = DateTimeService.toJsonFormat(dutyDay);

            const today = DateTimeService.today();
            const nowDate = DateTimeService.now();
            const now = showSwissTime ? DateTimeService.getSwissDate(nowDate) : nowDate;
            let currentLine: JSX.Element | null = null;
            if (showDutyAsFight && DateTimeService.isSameDate(today, dutyDay)) {
                currentLine = <span className="now-line" style={{ left: `${((DateTimeService.diffMinutes(now, today) / (24 * 60)) * 100).toFixed(3)}%` }} />;
            }
            let capacityNumberElement: JSX.Element | null = null;
            if (showCapacityNumbers && dutyItemCache && this._isShowCapacity(dayDuty, dutyItemCache) && this._isDateAllowed(dutyDay)) {
                capacityNumberElement = <span className="capacity-number">{capacityAvailable}/{capacityRequested}</span>;
            }

            if (haveCapacityInfo && rosterType === RosterType.RequestToolVacGround && !dayDuty.hasDuties) {
                capacityNumberElement = <span className="capacity-number">{capacityTotal - capacityUsed}</span>;
            }

            const id = personId + dutyDay.getTime();
            const extras = onRenderCellExtras?.(dutyDay, personId, dayDuty, dutyItemCache, focusedElement || null);
            if (extras) {
                itemClass.push('no-static');
            }
            dutyItems.push(
                <div id={id}
                    key={id}
                    className={itemClass.join(' ')}
                    aria-colindex={Math.round(dutyDay.getTime() / 86400000)}
                    aria-rowindex={index}
                    data-person-id={personId}
                    data-date={convertedDateToJson}
                    onDoubleClick={this._dutyDoubleClickHandler}
                    onDragStart={this._onStartDragDuty}
                    onDragOver={this._onDragOverDuty}
                    onDragLeave={this._onDragLeaveDuty}
                    onDragEnd={this._onDragEnd}
                    onDrop={this._onDropDuty}>
                    <div className="person-grid-duty-info"
                        data-popup-person-id={personId}
                        data-popup-person-code={personCode}
                        data-popup-date={convertedDateToJson}
                    >
                        {!isNotReleased ? this.renderCellInfo(dayDuty, dutyItemCache, dutyDay, personId) : null}
                        {haveCapacityInfo ? <span className="capacity-info" /> : null}
                        {!isRequestToolRosterType && haveLockSign ? <Icon name="lock" className={'shadowOffOptional' + (havePersistedLockSign ? ' persisted' : '') + (isImportant ? ' with-important' : '')} /> : null}
                        {capacityNumberElement}
                        {currentLine}
                    </div>
                    {isCopied && <span className="buffered-before" />}
                    {extras}
                </div>,
            );
        });
        const classList: string[] = this._gridLineClass();
        if (focusedElement && focusedElement.personId === personId) {
            classList.push('active');
        }
        return (
            <div key={personId + 'line'} className={classList.join(' ')}>
                {dutyItems}
            </div>
        );
    }

    renderCellInfo(dutyItem: IDayDuty | null, duty: DutyItem | null, date: Date, personId: string) {
        let ftlGav: string | null = null;
        let isFtlGavPersist: boolean = false;
        let isUserActionGavFtl: boolean = false;
        let wish: string | null = null;
        let priority: string | null = null;
        let important: string | null = null;
        let flex: string | null = null;
        let isChecker: boolean = false;
        let isTrainee: boolean = false;
        let isParty: boolean = false;
        let flightDateFrom: Date | null = null;
        let dateFrom: string | null = null;
        let flightDateTo: Date | null = null;
        let dateTo: string | null = null;
        let dateFromDiff: boolean | null = null;
        let dateToDiff: boolean | null = null;
        let hasPosition: boolean = false;
        let positionHasAbnd: boolean = false;
        let hasHomeStudy: boolean = false;
        let hasDutyPart: boolean = false;
        let hasLanding: boolean = false;
        let hasTakeoff: boolean = false;
        let hasFdpSplit: boolean = false;
        let hasFdpCombine: boolean = false;
        let hasDutySplit: boolean = false;
        let specialType: string = '';
        let overlayType: string = '';
        let requestType: JSX.Element | null = null;
        let flightViewDuties: JSX.Element | null = null;
        let isLongTermEvening: boolean = false;
        let isAfterLongTermEvening: boolean = false;
        let hasXStarFlight: boolean = false;
        let hasIntroFlight: boolean = false;
        let hasObserverFlight: boolean = false;
        let hasAllIntroFlights: boolean = false;
        let hasAllObserverFlights: boolean = false;
        const { showSwissTime, showDutyAsFight, rosterType } = this.props;
        const getDutyPoint = (diff: number) => `${((diff / (24 * 60)) * 100).toFixed(3)}%`;
        const isRequestToolRosterType: boolean = rosterType === RosterType.RequestToolOff || rosterType === RosterType.RequestToolFlight || rosterType === RosterType.RequestToolVacCabin || rosterType === RosterType.RequestToolVacCockpit || rosterType === RosterType.RequestToolGeneral;

        if (duty) {
            if (duty.isFtl || duty.isGav) {
                ftlGav = duty.isFtl ? 'F' : 'G';
                isFtlGavPersist = true;
                if (DateTimeService.isAfterDateOrEqual(date, RosterLine.newGavFtlSystemApplicableFrom)) {
                    isUserActionGavFtl = true;
                }
            } else {
                if (duty.shadowOffType === 'Ftl') ftlGav = 'F';
                if (duty.shadowOffType === 'Gav') ftlGav = 'G';

                // Freelancers + Others with persisted duty
                if (ftlGav && duty.shadowDutyType === 'None') {
                    isFtlGavPersist = true;
                }
            }
            if (duty.isPlanningPeriodHidden) {
                ftlGav = 'PP';
            }
            wish = duty.isWish ? 'W' : null;
            priority = duty.isPriority ? 'P' : null;
            important = duty.isImportant ? '!' : null;
            flex = duty.isFlex ? 'flex' : null;
            isChecker = duty.isChecker;
            isTrainee = duty.isTrainee;
            isParty = duty.isParty;
            hasLanding = duty.hasCustomLanding;
            hasTakeoff = duty.hasCustomTakeoff;
            if (duty.fdpAmendmentType === FdpAmendmentType.PreferFdpSplit) {
                hasFdpSplit = true;
            }
            if (duty.fdpAmendmentType === FdpAmendmentType.PreferFdpCombine) {
                hasFdpCombine = true;
            }
            if (duty.fdpAmendmentType === FdpAmendmentType.SplitDuty) {
                hasDutySplit = true;
            }
            specialType = duty.dooType && duty.dooType !== 'None' ? (duty.dooType === 'DOOO' ? 'O' : duty.dooType === 'DOOT' ? 'T' : duty.dooType === 'DOOV' ? 'V' : duty.dooType === 'DOOO1' ? 'O1' : duty.dooType === 'DOOO2' ? 'O2' : duty.dooType === 'DOOU' ? 'U' : "F") : '';

            if (!duty.isAfterNightDuty && !duty.isBeforeNightDuty && duty.requestedItemStatus === PersonRequestSummaryStatus.InProgress && dutyItem && !dutyItem.hasDuties) {
                if (duty.requestedItemType === PersonRequestType.Flight) {
                    if (duty.requestedActivity) {
                        if (duty.requestedActivity.toUpperCase() === 'POS') {
                            hasPosition = true;
                        }
                    }
                    if (duty.checkinDateTimeUtc) {
                        dateFrom = DateTimeService.toUiTime(this._getDate(duty.checkinDateTimeUtc), showSwissTime);
                        dateFromDiff = !DateTimeService.isSameDate(duty.checkinDateTimeUtc, dutyItem.dutyDay);
                    }

                    if (duty.checkoutDateTimeUtc) {
                        dateTo = DateTimeService.toUiTime(this._getDate(duty.checkoutDateTimeUtc), showSwissTime);
                        dateToDiff = !DateTimeService.isSameDate(duty.checkoutDateTimeUtc, dutyItem.dutyDay);
                    }
                    requestType = <span className="person-request" />;
                }

            }

            if (duty.requestId) {
                specialType += 'R';
            }

            if (duty.flagDutyList && duty.flagDutyList.length > 0) {
                isLongTermEvening = duty.flagDutyList.findIndex(x => x.activityCodeName === ActivityCode.LTE) !== -1;
                overlayType += duty.flagDutyList.map(x => x.activityCodeName !== ActivityCode.LTE && x.activityCodeName.substring(0, 1) || '').join('');
            }
            isAfterLongTermEvening = duty.isAfterLongTermEvening;
            hasXStarFlight = !isRequestToolRosterType && duty.hasXStarFlight;
            hasIntroFlight = !isRequestToolRosterType && duty.activities?.some((a) => a.isIntroFlight);
            hasObserverFlight = !isRequestToolRosterType && duty.activities?.some((a) => a.isObserverFlight);
            hasAllIntroFlights = !isRequestToolRosterType && duty.activities?.filter((a) => !!a.flightId).every((a) => a.isIntroFlight);
            hasAllObserverFlights = !isRequestToolRosterType && duty.activities?.filter((a) => !!a.flightId).every((a) => a.isObserverFlight);
        }

        const dutyClass = ['flight-wrap'];

        const hasAbnd = duty && (duty.isAfterNightDuty || duty.isBeforeNightDuty);

        if (dutyItem) {
            if (dutyItem.flightDateFrom) {
                flightDateFrom = this._getDate(dutyItem.flightDateFrom);
                dateFrom = DateTimeService.toUiTime(flightDateFrom, showSwissTime);
                dateFromDiff = !DateTimeService.isSameDate(flightDateFrom, dutyItem.dutyDay);
            }

            if (dutyItem.flightDateTo) {
                flightDateTo = this._getDate(dutyItem.flightDateTo);
                dateTo = DateTimeService.toUiTime(flightDateTo, showSwissTime);
                dateToDiff = !DateTimeService.isSameDate(flightDateTo, dutyItem.dutyDay);
            }

            hasPosition = dutyItem.hasPosition || hasPosition;
            hasHomeStudy = dutyItem.hasHomeStudy;
            hasDutyPart = dutyItem.hasDutyPart;

            if (!dutyItem.hasDuties && !hasAbnd && !dateFrom && !dateTo) {
                if (isLongTermEvening || isAfterLongTermEvening) {
                    dutyClass.push('breakWord-withFlag');
                } else {
                    dutyClass.push('breakWord');
                }
            }

            if (hasPosition && hasAbnd) {
                positionHasAbnd = true;
            }
        }

        if (showDutyAsFight) {
            if (duty?.activities.length) {
                const flightActivities = duty.activities.filter(e => e.dateStartUtc && e.dateEndUtc);
                flightViewDuties = (
                    <>
                        {flightActivities.map(d => {
                            let title = d.activityCodeName;
                            let dateStartUtc = d.dateStartUtc;
                            let dateEndUtc = d.dateEndUtc;
                            const isDutyPart = d.activityCodeName === ActivityCode.DUTY_PART;
                            if (isDutyPart) {
                                const mainActivity = flightActivities.find(a => ActivityCode.groundActivities.includes(a.activityCodeName));
                                title = mainActivity?.activityCodeName || d.activityCodeName;
                            }
                            if (d.estimatedStartUtc && d.estimatedEndUtc) {
                                dateStartUtc = d.estimatedStartUtc;
                                dateEndUtc = d.estimatedEndUtc;
                            }
                            if (d.activityCodeName.startsWith('FSB') && duty.checkinDateTimeUtc) {
                                dateEndUtc = duty.checkinDateTimeUtc;
                            }
                            if (dateStartUtc && dateEndUtc) {
                                const flightInfo = d.flightId ? targetConstructor.getFlightInformationFromFlightId(d.flightId) : null;
                                const from = this._getDate(dateStartUtc);
                                const to = this._getDate(dateEndUtc);
                                if (!DateTimeService.isValidDate(from) || !DateTimeService.isValidDate(to)) {
                                    return null;
                                }

                                const startDiff = DateTimeService.diffMinutes(from, DateTimeService.startOfDate(duty?.date));
                                const toDiff = DateTimeService.diffMinutes(to, from);
                                return (
                                    <React.Fragment key={d.id}>
                                        <span
                                            className="duty-flight"
                                            style={{
                                                left: getDutyPoint(startDiff),
                                                width: getDutyPoint(toDiff),
                                            }}
                                        >
                                            {flightInfo ? `${flightInfo.number}-${flightInfo.originRcd}-${flightInfo.destinationRcd}` : title}
                                        </span>
                                        {isDutyPart && (
                                            <span
                                                className="duty-flight-time-wrapper"
                                                style={{
                                                    left: getDutyPoint(startDiff),
                                                    width: getDutyPoint(toDiff),
                                                }}
                                            >
                                                <span className="duty-flight-time-reported">
                                                    <span className={DateTimeService.isSameDate(from, duty?.date) ? '' : 'bold-font'}>{DateTimeService.toUiTime(from)}</span>
                                                    -
                                                    <span className={DateTimeService.isSameDate(to, duty?.date) ? '' : 'bold-font'}>{DateTimeService.toUiTime(to)}</span>
                                                </span>
                                            </span>
                                        )}
                                    </React.Fragment>
                                );
                            } else {
                                if (duty.isAfterNightDuty || duty.isBeforeNightDuty) {
                                    title = duty.isAfterNightDuty ? ActivityCode.AND : ActivityCode.BND;
                                }
                                return <span key={d.id} className="duty-flight-title">{title}</span>;
                            }
                        })}
                    </>
                );
            }
        }

        let dutyTitle = (dutyItem && dutyItem.title) || '';
        if (duty?.isAfterNightDuty === true && !!duty.customTitle)
            dutyTitle += '-' + duty.customTitle;

        if (dutyTitle.length > 5) dutyClass.push('flight-wrap-overflow');

        const hasCrewHidden = !!duty?.isCrewHidden;
        const needWrap = dutyItem && dutyItem.needWrapTitle;
        const hasFlags = flex || hasPosition || hasCrewHidden || hasHomeStudy || hasDutyPart || isParty || isLongTermEvening || isAfterLongTermEvening || hasXStarFlight || hasIntroFlight || hasObserverFlight;

        let title: JSX.Element | string | null = null;
        if (!dutyTitle.trim()) {
            title = <>&nbsp;</>;
        } else {
            title = dutyTitle;
        }

        if (hasFlags) {
            if (dutyItem && ActivityCode.TZVs.indexOf(dutyItem.codeName) != -1) {
                title = <span className="flight-title">{dutyTitle}{flex ? <><br/>{flex}</> : <></>}</span>;
            }
            else {
                title = <span className="flight-title">{dutyTitle}{flex ? <>-{flex}</> : <></>}</span>;
            }
        }
        
        return (
            <div className={dutyClass.join(' ')}>
                {(dutyTitle || hasFlags) && (
                    <span className={'flight-number flex' + (needWrap ? '' : ' nowrap')}>
                        {title}
                        {hasCrewHidden && <Icon name="eye-slash" className={"dutyflag-flex dutyflag-flex-red"} />}
                        {hasPosition && <Icon name="bus-alt" className="dutyflag-flex" />}
                        {hasHomeStudy && <Icon name="book" className="dutyflag-flex" />}
                        {hasDutyPart && <Icon name="clock" className="dutyflag-flex" />}
                        {isParty && <Icon name="cocktail" className="dutyflag-flex" />}
                        {isLongTermEvening && <div title="LTE" className={`dutyflag-has-lte-icon ${duty?.hasLteErrors ? 'error' : ''} before`}><span /></div>}
                        {isAfterLongTermEvening && <div title="After LTE" className={`dutyflag-has-lte-icon ${duty?.hasLteErrors ? 'error' : ''} after`}><span /></div>}
                        {hasXStarFlight && <div title="XStar" className="dutyflag-xstar"><span>X*</span></div>}
                        {hasIntroFlight && <Icon name="vial" className={"dutyflag-flex" + (!hasAllIntroFlights ? " dutyflag-flex-red" : "")} />}
                        {hasObserverFlight && <Icon name="mask" className={"dutyflag-flex" + (!hasAllObserverFlights ? " dutyflag-flex-red" : "")}  />}
                    </span>
                )}
                {dutyItem && dutyItem.hasDuties && dutyItem.subTitle}
                {showDutyAsFight && flightViewDuties}
                {duty && showDutyAsFight && flightDateFrom && flightDateTo && DateTimeService.isValidDate(flightDateFrom) && DateTimeService.isValidDate(flightDateTo) &&
                    <span className="duty-flight-connector" style={{
                        left: getDutyPoint(DateTimeService.diffMinutes(flightDateFrom, DateTimeService.startOfDate(duty.date))),
                        width: getDutyPoint(DateTimeService.diffMinutes(flightDateTo, flightDateFrom)),
                    }}>
                        {!hasDutyPart && <span className="duty-flight-time">
                            <span className={`duty-flight-time-from ${DateTimeService.isSameDate(flightDateFrom, duty?.date) ? '' : 'bold-font'}`}>{DateTimeService.toUiTime(flightDateFrom, this.props.showSwissTime)}</span>
                            -
                            <span className={`duty-flight-time-to ${DateTimeService.isSameDate(flightDateTo, duty?.date) ? '' : 'bold-font'}`}>{DateTimeService.toUiTime(flightDateTo, this.props.showSwissTime)}</span>
                        </span>}
                    </span>
                }
                {this.props.onRenderCellDetails?.(date, personId, dutyItem, duty)}
                {(hasTakeoff || hasLanding) && (
                    <span className="dutyflag-has-landing-takeoff">
                        {hasLanding && <Icon title="Landing" name="plane-arrival" />}
                        {hasTakeoff && <Icon title="Takeoff" name="plane-departure" />}
                    </span>
                )}
                {hasFdpSplit && <Icon title="Prefer FDP split" solid name="arrows-alt-h" className="dutyflag-has-right-icon" />}
                {hasFdpCombine && <Icon title="Prefer FDP combine" name="angle-double-right" className="dutyflag-has-right-icon" />}
                {hasDutySplit && <Icon title="Duty Split" name="hotel" className="dutyflag-has-right-icon" />}
                {ftlGav && (!dateFrom && !dateTo) && <span className={`dutyflag-gavftl ${isFtlGavPersist ? '' : ' dutyflag-gavftl-notpersist'} ${!isUserActionGavFtl ? '' : ' manual-action-border'}`}>{ftlGav}</span>}
                {important && <span className="dutyflag-important">{important}</span>}
                {isChecker && <Icon name="star" className={'dutyflag-is-checker' + (isChecker && isTrainee ? ' both' : '')} />}
                {isTrainee && <Icon name="graduation-cap" className="dutyflag-is-trainee" />}
                {specialType && <span className="dutyflag-special-type">{specialType}</span>}
                {overlayType && <span className="dutyflag-overlay-type">{overlayType}</span>}
                {(wish || priority) && (
                    <span className="dutyflag-wish">
                        {wish}
                        {priority}
                    </span>
                )}
                {requestType}
                {!showDutyAsFight && dateFrom && !positionHasAbnd && <span className={dateFromDiff ? 'duty-confirm-table-flight duty-details-time-from bold-font ' : 'duty-confirm-table-flight duty-details-time-from'}>{dateFrom}</span>}
                {!showDutyAsFight && dateTo && !positionHasAbnd && <span className={dateToDiff ? 'duty-confirm-table-flight duty-details-time-to bold-font' : 'duty-confirm-table-flight duty-details-time-to'}>{dateTo}</span>}
            </div>
        );
    }

    private _onStartDragDuty = (e: React.DragEvent) => {
        const sourceElement = e.currentTarget;
        this._isDragStart = true;

        e.dataTransfer.effectAllowed = 'copyMove';
        this.props.onStartDragDuty?.(sourceElement, true);
    };

    private _onDragOverDuty = (e: React.DragEvent) => {
        e.preventDefault();
        const targetDutyElement = e.currentTarget;
        const isCopy = e.ctrlKey;

        const dropEffect = this.props.onDragOverDuty?.(targetDutyElement, isCopy);
        e.dataTransfer.dropEffect = dropEffect || 'none';

        if ((e.target as Element).classList.contains('person-grid-duty-info')) {
            e.currentTarget.firstElementChild?.classList.add('dragover');
        }
    };

    private _onDragLeaveDuty = (e: React.DragEvent) => {
        e.preventDefault();
        e.currentTarget.firstElementChild?.classList.remove('dragover');
    };

    private _onDragEnd = (e: React.DragEvent) => {
        this._isDragStart = false;
        e.currentTarget.removeAttribute('draggable');
        this.props.onDragEndDuty?.();
    };

    private _onDropDuty = (e: React.DragEvent) => {
        const targetDutyElement = e.currentTarget;
        const isCopy = e.ctrlKey;

        this.props.onDropDuty?.(targetDutyElement, isCopy);

        targetDutyElement.firstElementChild?.classList.remove('dragover');
        e.dataTransfer.clearData();
    };

    private _getDate(date: Date) {
        return this.props.showSwissTime ? DateTimeService.getSwissDate(date) : date;
    }

    private _getCapacityLevel(duty: DutyItem, rosterType?: string) {
        if (rosterType === RosterType.RequestToolVacCabin || rosterType === RosterType.RequestToolFreelance || rosterType === RosterType.RequestToolVacGround) {
            return duty.capacityLevel;
        } else if (rosterType === RosterType.RequestToolVacCockpit) {
            return duty.capacityLevelCockpit;
        }
        return null;
    }

    private _getCapacityLevelClass(duty: DutyItem, rosterType?: string) {
        const hasCapacity = duty.haveCapacity;
        const capacityLevel = this._getCapacityLevel(duty, rosterType);
        const hasHighCapacityLevel = !capacityLevel || capacityLevel === CapacityLevel.High;
        const hasNoneCapacityLevel = !capacityLevel || capacityLevel === CapacityLevel.None;
        const hasLowCapacityLevel = !capacityLevel || capacityLevel === CapacityLevel.Low;

        if (hasCapacity === true && hasHighCapacityLevel) {
            return 'duty-have-capacity-off';
        } else if (hasCapacity === false && hasNoneCapacityLevel) {
            return 'duty-have-no-capacity-off';
        } else if (hasCapacity === true && hasLowCapacityLevel) {
            return 'duty-have-low-capacity-off';
        }
        return null;
    }

    private _isShowCapacity(dutyItem: IDayDuty, dayDuty: DutyItem) {
        const hasNoDuties = !dutyItem?.hasDuties || ActivityCode.FreelancerActivities.indexOf(dutyItem.codeName) !== -1;
        const hasCheckinCheckout = dayDuty.checkinDateTimeUtc || dayDuty.checkoutDateTimeUtc;
        const hasAbnd = dayDuty.isAfterNightDuty || dayDuty.isBeforeNightDuty;
        return hasNoDuties && !hasCheckinCheckout && !hasAbnd && (!dayDuty.shadowDutyType || dayDuty.isShadowOffOptional) && dayDuty.haveCapacity !== undefined;
    }

    private _isDateAllowed(dutyDay: Date) {
        const { allowedDateRange } = this.props;
        if (Array.isArray(allowedDateRange)) {
            return allowedDateRange?.some(range => range.from <= dutyDay && range.to >= dutyDay);
        }

        return allowedDateRange && allowedDateRange.from <= dutyDay && allowedDateRange.to >= dutyDay;
    }

    private _gridLineClass(): string[] {
        const { isBeforeActivePerson, rosterType } = this.props;
        const classNames = ['person-grid-line'];
        const isFullRosterType: boolean = rosterType === RosterType.Full;
        if (!isFullRosterType) return classNames;
        if (isBeforeActivePerson) classNames.push('beforeActive');
        return classNames;
    }

    private _isNotReleased(dutyDay: Date, dutyItem: DutyItemCache | null, releasedLastDate: Date | null) {
        if (!releasedLastDate) return false;
        const date = releasedLastDate || this.props.historizeLastDate || null;
        if (!date) return false;

        const lastReleasedDateWithRotationTails = DateTimeService.addDays(date, 15);
        const isRotationTail = DateTimeService.isAfterDate(dutyDay, releasedLastDate) && DateTimeService.isBeforeDate(dutyDay, lastReleasedDateWithRotationTails);
        if (isRotationTail) {
            const hasActivities = !!dutyItem?.activities.length;
            return !hasActivities;
        }

        return DateTimeService.isAfterDate(dutyDay, releasedLastDate);
    }
}

export default RosterLine;
